/**
 * Edit Provider User Dialog Component
 * 
 * Dialog for editing existing provider users
 * Includes form validation with react-hook-form and Zod
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Alert,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import { AppButton, AppIconButton, AppTextField } from '../design-system';
import { Close as CloseIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { PhoneField } from '../forms';
import { zodResolver } from '@hookform/resolvers/zod';
import { providerUsersService } from '../../services/ProviderUsersService';
import { UpdateProviderUserRequestSchema } from '../../../../shared/src/schemas';
import type { z } from 'zod';
import type { ProviderUser } from '../../../../shared/src/schemas';
import * as dt from '../../../../shared/src/data.types';
import SensitiveActionDialog from '../dialogs/SensitiveActionDialog';

// Use input type for form (before validation)
type UpdateProviderUserFormData = z.input<typeof UpdateProviderUserRequestSchema>;

// ProviderUser with string id (for DataGrid compatibility)
type ProviderUserDisplay = Omit<ProviderUser, 'id'> & { id: string };

interface EditProviderUserDialogProps {
  open: boolean;
  user: ProviderUserDisplay | null;
  onClose: () => void;
  onSuccess: () => void;
  canChangeRole?: boolean;
}

const EditProviderUserDialog: React.FC<EditProviderUserDialogProps> = ({
  open,
  user,
  onClose,
  onSuccess,
  canChangeRole = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingUpdate, setPendingUpdate] = useState<z.infer<typeof UpdateProviderUserRequestSchema> | null>(null);
  const [stepUpOpen, setStepUpOpen] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<UpdateProviderUserFormData>({
    resolver: zodResolver(UpdateProviderUserRequestSchema),
  });

  // Reset form when user changes
  useEffect(() => {
    if (user) {
      reset({
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone || '',
        role: user.role,
      });
    }
  }, [user, reset]);

  const handleClose = () => {
    reset();
    setError(null);
    onClose();
  };

  const onSubmit = async (data: UpdateProviderUserFormData) => {
    if (!user) return;

    const validatedData = UpdateProviderUserRequestSchema.parse(data);
    const roleChanged = validatedData.role !== undefined && validatedData.role !== user.role;

    if (roleChanged && canChangeRole) {
      setPendingUpdate(validatedData);
      setStepUpOpen(true);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await providerUsersService.updateProviderUser(parseInt(user.id) as dt.IntegerId, validatedData);
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleStepUpConfirm = async (stepUpToken: string) => {
    if (!user || !pendingUpdate) return;

    try {
      setLoading(true);
      setError(null);
      await providerUsersService.updateProviderUser(
        parseInt(user.id) as dt.IntegerId,
        pendingUpdate,
        stepUpToken,
      );
      setPendingUpdate(null);
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Edit Provider User
            <AppIconButton edge="end" onClick={handleClose} size="small" disabled={loading}>
              <CloseIcon />
            </AppIconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <AppTextField
              label="Email Address"
              value={user.email}
              disabled
              fullWidth
              helperText="Email cannot be changed"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    label="First Name"
                    fullWidth
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    disabled={loading}
                  />
                )}
              />

              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    label="Last Name"
                    fullWidth
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                    disabled={loading}
                  />
                )}
              />
            </Box>

            <Controller
              name="phone"
              control={control}
              render={({ field, fieldState }) => (
                <PhoneField
                  value={field.value ?? null}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  disabled={loading}
                />
              )}
            />

            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <AppTextField
                  {...field}
                  label="Role"
                  select
                  fullWidth
                  error={!!errors.role}
                  helperText={
                    !canChangeRole 
                      ? "Only administrators can change user roles" 
                      : errors.role?.message
                  }
                  disabled={loading || !canChangeRole}
                >
                  <MenuItem value="provider_admin">Administrator</MenuItem>
                  <MenuItem value="provider_engineer">Engineer</MenuItem>
                  <MenuItem value="provider_support">Support</MenuItem>
                  <MenuItem value="provider_deployment_engineer">Deployment Engineer (contractor)</MenuItem>
                </AppTextField>
              )}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <AppButton onClick={handleClose} disabled={loading}>
            Cancel
          </AppButton>
          <AppButton type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Save Changes'}
          </AppButton>
        </DialogActions>
      </form>

      <SensitiveActionDialog
        open={stepUpOpen}
        action="change-role"
        target={
          user
            ? {
                id: String(user.id),
                email: user.email,
                displayName: `${user.firstName} ${user.lastName}`,
              }
            : null
        }
        context={{
          newRoleLabel: pendingUpdate?.role
            ? pendingUpdate.role.replace('provider_', '').replace('_', ' ')
            : undefined,
        }}
        onClose={() => {
          setStepUpOpen(false);
          setPendingUpdate(null);
        }}
        onConfirm={handleStepUpConfirm}
      />
    </Dialog>
  );
};

export default EditProviderUserDialog;
